import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@clerk/react';
import { useAgent } from 'agents/react';
import { useAgentChat } from '@cloudflare/ai-chat/react';

import { gatewayHost } from '../lib/env';
import { useSessions } from './useSessions';
import { notifySessionsChanged } from './useSessionList';

/**
 * One Bluebird WS connection + AI-SDK chat helpers per tab.
 *
 * - `useAgent({ basePath, name, query })` opens the WebSocket. The
 *   gateway worker derives the actual Bluebird DO from the Clerk token,
 *   not from `name` — so `name` is purely cosmetic for routing. We pass
 *   `sessionId` as `name` so the WS URL reflects the active tab in
 *   devtools and so the URL path-segment fallback (DD-BLB-18) works for
 *   clients that can't set a custom header.
 * - `query: () => ({ token, sid })` injects a fresh Clerk session token
 *   into the WS upgrade URL. Browsers can't set custom headers on WS,
 *   so the Worker promotes `?token=...` into `Authorization: Bearer ...`
 *   (see `apps/cloudflare/src/auth.ts` shim). `sid` carries the
 *   sessionId belt-and-suspenders, in case some downstream wants the
 *   query-form copy.
 * - `useAgentChat({ agent })` wraps the WebSocket as an AI SDK
 *   `useChat`-shaped hook, plus exposes `clearHistory`, `isStreaming`,
 *   etc. Streaming is over the same WebSocket — we get partial deltas
 *   as the model produces them.
 */
/**
 * The shape returned by `useBluebirdChat`. Inferred from the
 * implementation rather than declared explicitly because `useAgent` has
 * two overloads (typed/untyped agent stub) and TypeScript picks
 * different ones for `ReturnType<typeof useAgent>` vs the actual call,
 * producing a spurious mismatch on the `call`/`stub` props.
 */
export type BluebirdChat = ReturnType<typeof useBluebirdChat>;

export function useBluebirdChat(sessionId: string) {
  const { getToken, isSignedIn } = useAuth();
  const host = gatewayHost();

  // partysocket reconnects automatically. We pin the host + path so the
  // socket survives across renders; only `sessionId` (passed via `name`
  // and `query`) and the auth token are dynamic.
  const agent = useAgent({
    host,
    agent: 'bluebird',
    basePath: 'agents/agent',
    name: sessionId,
    // `query` is invoked at connect time; passing a function instead of
    // a static object lets us refresh the Clerk token on reconnect. The
    // QueryObject contract requires every value to be a string (never
    // undefined), so we always return a flat shape with explicit keys.
    query: async (): Promise<Record<string, string>> => {
      const out: Record<string, string> = { sid: sessionId };
      if (isSignedIn) {
        const token = await getToken();
        if (token) out.token = token;
      }
      return out;
    },
    // Re-run query whenever sessionId or sign-in state flips.
    queryDeps: [sessionId, isSignedIn],
    // Cache the resolved query for 60 s so reconnect storms don't hammer
    // Clerk for tokens (Clerk's getToken caches internally too).
    cacheTtl: 60_000,
  });

  // Build a small adapter object that exposes only what useAgentChat
  // needs — keeps the agent hook's concrete return type out of the
  // useAgentChat options' generic resolution path.
  const agentAdapter = useMemo(
    () => ({
      send: agent.send.bind(agent),
      addEventListener: agent.addEventListener.bind(agent),
      removeEventListener: agent.removeEventListener.bind(agent),
      agent: agent.agent,
      name: agent.name,
      getHttpUrl: agent.getHttpUrl.bind(agent),
    }),
    [agent],
  );

  const chat = useAgentChat({
    agent: agentAdapter,
    // useAgentChat defaults to fetching `${agent.getHttpUrl()}/get-messages`
    // on mount to seed history. Our Worker doesn't expose that endpoint
    // (the chat protocol is WS-only and Bluebird hydrates history from
    // the DO's SQLite session on connect), so the default fetch 404s
    // and Suspense-throws via `use(promise)`. Disabling it lets the
    // first WS connect deliver history via the standard chat protocol
    // instead.
    getInitialMessages: null,
  });

  // Real connection state — partysocket exposes the underlying
  // WebSocket's readyState. The agents SDK's `identified` flag only
  // flips when the server broadcasts `cf_agent_identity`, which is
  // emitted by the agents framework's own routing path
  // (`routeAgentRequest` / `getAgentByName`). Our Worker bypasses that
  // (identity-derived DO addressing) so `identified` stays false
  // forever — readyState is the only honest signal here.
  const connectionState = useConnectionState(agent);

  // Track unread / streaming activity for the tab badge. We surface the
  // computed flags here so the parent can react without re-subscribing.
  const isStreaming = chat.isStreaming;
  const isServerStreaming = chat.isServerStreaming;

  // Listen for server-pushed `session.title.updated` broadcasts so the
  // tab strip + sidebar reflect Haiku-generated titles immediately,
  // without waiting for the next /api/sessions refetch.
  const { applyServerSessionUpdate } = useSessions();
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (typeof event.data !== 'string') return;
      let parsed: unknown;
      try {
        parsed = JSON.parse(event.data);
      } catch {
        return;
      }
      if (
        parsed &&
        typeof parsed === 'object' &&
        (parsed as { type?: unknown }).type === 'session.title.updated'
      ) {
        const payload = parsed as { sessionId?: unknown; title?: unknown };
        if (
          typeof payload.sessionId === 'string' &&
          typeof payload.title === 'string'
        ) {
          applyServerSessionUpdate({
            id: payload.sessionId,
            title: payload.title,
          });
          notifySessionsChanged();
        }
      }
    };
    agent.addEventListener('message', handler);
    return () => {
      agent.removeEventListener('message', handler);
    };
  }, [agent, applyServerSessionUpdate]);

  return { agent, chat, isStreaming, isServerStreaming, connectionState };
}

export type ConnectionState = 'connecting' | 'open' | 'closing' | 'closed';

interface ConnectionListenable {
  readyState: number;
  addEventListener: (type: string, listener: () => void) => void;
  removeEventListener: (type: string, listener: () => void) => void;
}

/**
 * Subscribes to a partysocket's open/close lifecycle and returns a
 * stable string for the current state. The initial render samples
 * `readyState` synchronously so the first paint reflects whatever the
 * socket is doing rather than always starting at "connecting".
 */
function useConnectionState(socket: ConnectionListenable): ConnectionState {
  const [state, setState] = useState<ConnectionState>(() =>
    readyStateToString(socket.readyState),
  );

  useEffect(() => {
    const sync = () => setState(readyStateToString(socket.readyState));
    sync();
    socket.addEventListener('open', sync);
    socket.addEventListener('close', sync);
    socket.addEventListener('error', sync);
    return () => {
      socket.removeEventListener('open', sync);
      socket.removeEventListener('close', sync);
      socket.removeEventListener('error', sync);
    };
  }, [socket]);

  return state;
}

function readyStateToString(rs: number): ConnectionState {
  switch (rs) {
    case 0:
      return 'connecting';
    case 1:
      return 'open';
    case 2:
      return 'closing';
    default:
      return 'closed';
  }
}

/**
 * Hook that reports whether a given tab has unseen activity since it
 * was last viewed. Pairs with the tab strip to render an unread dot.
 */
export function useUnread(
  isStreaming: boolean,
  isActive: boolean,
  messageCount: number,
): boolean {
  const [seenCount, setSeenCount] = useState(messageCount);
  const prevActive = useRef(isActive);

  // Catch up the seen marker whenever the tab is active.
  useEffect(() => {
    if (isActive) setSeenCount(messageCount);
    prevActive.current = isActive;
  }, [isActive, messageCount]);

  if (isActive) return false;
  return isStreaming || messageCount > seenCount;
}
